// §4.6 Schedule board (`/planning/schedule`, P3-I-2): the facility's published
// finite schedule as swim lanes, read on `planning.schedule.read` and decided
// on `planning.schedule.publish` (D-406 rulings 1 and 3). The header names the
// version, when and how it was published, and its KPIs; a horizon of 7, 14 or
// 28 days within the schedule's horizon, a day or week zoom and three filters
// shape the lanes; a block opens its drawer, an overload marker its late work,
// and a waiting draft its diff with Publish and Discard. Times are the
// facility's, with its offset. At 412 px the board reads one lane and one day
// as cards (§4.6 "At 412 px").
//
// P3-I-3 (D-402, D-403): a marker's panel lists the overload's ranked, priced
// moves with Apply; a planner drags a block along its lane or onto an allowed
// lane, or moves it from its drawer's form, and the board draws the preview's
// ghost blocks with a consequences panel to confirm or cancel; the Pins panel
// lists the standing pins and defers with Unpin. A phone applies moves and
// reads pins, and moves nothing by drag (§4.6 "At 412 px").

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { FilterBar, type FilterChip } from "../../../../components/filter-bar";
import { SegmentedControl, Select } from "../../../../components/select";
import { StatStrip, StatTile } from "../../../../components/stat-strip";
import { EmptyState, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { useDesktop } from "../../../../components/use-desktop";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useShiftNames } from "../hooks/calendar-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  useScheduleBoard,
  useScheduleDirectives,
  useScheduleHeads,
  useScheduleOverloads,
  useScheduleRefresh,
  useScheduleRuns,
  type ScheduleHeader,
  type ScheduleOperation,
  type ScheduleOverload,
  type ScheduleRunList,
} from "../hooks/schedule-queries";
import { formatDate, useOffline, weekLabel } from "../lib/planning-lib";
import {
  BOARD_DAYS,
  boardStart,
  hasFilters,
  passesFilters,
  todayAt,
  validateScheduleSearch,
  windowOf,
  zonedStamp,
  type BoardDays,
  type BoardFilters,
  type ScheduleSearch,
} from "../lib/schedule-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import {
  SolveErrorLine,
  SolveStatusChip,
  SolveTriggerChip,
  useSolveControl,
} from "../components/schedule-bits";
import { ScheduleCards, ScheduleHatchDefs, ScheduleLanes } from "../components/schedule-lanes";
import {
  ConsequencesPanel,
  PinsPanel,
  useMoveFlow,
  type MoveRequest,
} from "../components/schedule-drag";
import { ghostsOf, type MoveBlock } from "../lib/schedule-moves-lib";
import {
  BlockDrawer,
  DraftPanel,
  OverloadPanel,
  laneName,
  type DraftOutcome,
} from "../components/schedule-panels";

type Panel =
  | { kind: "op"; op: ScheduleOperation }
  | { kind: "overload"; overload: ScheduleOverload }
  | { kind: "draft"; draft: ScheduleHeader }
  | { kind: "pins" };

/** The board's one notice line: a decided draft, or a move saved, applied or overtaken. */
type BoardNotice =
  | { readonly source: "draft"; readonly outcome: DraftOutcome }
  | {
      readonly source: "move";
      readonly kind: "moved" | "applied" | "superseded";
      readonly version?: number | undefined;
    };

/** A drag the board refused before asking the server, and why. */
interface DragRefusal {
  readonly reason: MoveBlock | "lane";
  readonly laneId: string;
}

export function PlanningScheduleScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const shiftNames = useShiftNames(facility.id, perms.has("production.read"));
  const canPublish = perms.has("planning.schedule.publish");
  const offline = useOffline();
  const zone = facility.timezone;
  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const search = validateScheduleSearch(rawSearch);
  const days: BoardDays = desktop ? (search.days ?? 7) : 7;
  const zoom = search.zoom ?? "week";
  const today = todayAt(zone);

  const [horizon, setHorizon] = useState<{ start: string; days: number } | undefined>(undefined);
  const windowDays = desktop ? days : 1;
  const from = boardStart(search.from, today, horizon, windowDays);
  const window = useMemo(() => windowOf(from, windowDays, zone), [from, windowDays, zone]);
  // The header poll is the board's clock: two one-row reads a minute, and
  // the board re-reads its pages only when a schedule id changes.
  const heads = useScheduleHeads(facility.id);
  const board = useScheduleBoard(facility.id, { from: window.from, to: window.to }, heads);
  const runs = useScheduleRuns(facility.id, undefined);
  const refresh = useScheduleRefresh();
  const solve = useSolveControl(facility.id, { canSolve: canPublish });
  const [panel, setPanel] = useState<Panel | undefined>(undefined);
  const [notice, setNotice] = useState<BoardNotice | undefined>(undefined);
  const [refusal, setRefusal] = useState<DragRefusal | undefined>(undefined);
  const directives = useScheduleDirectives(facility.id);

  const read = board.data;
  const header = read?.board.schedule ?? null;
  const overloads = useScheduleOverloads(
    facility.id,
    read?.mode === "live" ? header?.id : undefined,
  );
  const doRefresh = useCallback(() => refresh(facility.id), [refresh, facility.id]);
  const moved = useCallback((h: ScheduleHeader) => {
    setNotice({ source: "move", kind: "moved", version: h.version });
  }, []);
  const overtaken = useCallback(() => {
    setNotice({ source: "move", kind: "superseded" });
  }, []);
  const flow = useMoveFlow({
    scheduleId: read?.mode === "live" ? header?.id : undefined,
    refresh: doRefresh,
    onCommitted: moved,
    onSuperseded: overtaken,
  });
  const { cancel: cancelMove } = flow;

  // The facility's reset runs before the horizon is read again, so a switch
  // never keeps the last facility's horizon and never drops the new one.
  useEffect(() => {
    setPanel(undefined);
    setNotice(undefined);
    setRefusal(undefined);
    setHorizon(undefined);
    cancelMove();
  }, [facility.id, cancelMove]);

  useEffect(() => {
    if (header === null) return;
    setHorizon((prev) =>
      prev?.start === header.horizon_start && prev.days === header.horizon_days
        ? prev
        : { start: header.horizon_start, days: header.horizon_days },
    );
  }, [header]);

  const authError = [board.error, runs.error].find((e) => e instanceof UnauthorizedError);
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const setSearch = (next: Partial<ScheduleSearch>) => {
    const merged: ScheduleSearch = { ...search, ...next };
    const compact = Object.fromEntries(
      Object.entries(merged).filter(([, v]) => v !== undefined && v !== ""),
    );
    void navigate({ to: "/planning/schedule", search: compact, replace: true });
  };

  const pinCount = directives.data?.length;
  const pageHead = (
    <PlanningHeader
      title={t("planning.schedule.title")}
      actions={
        <>
          <Button
            data-testid="planning-schedule-pins"
            data-count={pinCount === undefined ? "" : String(pinCount)}
            onClick={() => {
              setPanel({ kind: "pins" });
            }}
          >
            {pinCount === undefined
              ? t("planning.schedule.pins.buttonLoading")
              : t("planning.schedule.pins.button", { count: pinCount })}
          </Button>
          {solve.button}
        </>
      }
    />
  );

  if (board.error !== null && authError === undefined) {
    return (
      <section className="planning-screen planning-schedule" data-testid="planning-schedule">
        {pageHead}
        <ErrorState
          title={t("planning.schedule.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void board.refetch()}
          requestId={requestIdOf(board.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  if (read === undefined) {
    return (
      <section className="planning-screen planning-schedule" data-testid="planning-schedule">
        {pageHead}
        <LaneSkeleton />
      </section>
    );
  }

  const live = read.board;
  const waiting =
    read.mode === "live" ? live.waiting_draft : read.mode === "first_draft" ? live.schedule : null;
  const shown = read.mode === "live" || read.mode === "first_draft" ? live.schedule : null;
  const filters: BoardFilters = { order: search.order, product: search.product, key: search.key };
  const filtering = hasFilters(filters);
  const ops = live.operations.filter((o) => passesFilters(o, filters));
  const unplaced = live.unplaced.filter((o) => passesFilters(o, filters));
  const laneFilter = search.wc;
  const lanes = live.lanes.filter(
    (l) => laneFilter === undefined || l.work_center_id === laneFilter,
  );
  const openOverloads = (overloads.data ?? []).filter(
    (o) => laneFilter === undefined || o.work_center_id === laneFilter,
  );

  // A schedule that holds no operation at all, placed or unplaced: its lanes
  // are the work centres it names, so it has none, and the board would draw
  // a date header over nothing. It reads as the no-schedule state.
  const nothingPlaced =
    live.lanes.length === 0 && live.operations.length === 0 && live.unplaced_count === 0;
  const emptyBoard = (
    <div data-testid="planning-schedule-empty">
      <EmptyState
        title={t("planning.schedule.emptyTitle")}
        body={t("planning.schedule.emptyBody")}
        {...(canPublish && !solve.running
          ? { ctaLabel: t("planning.schedule.solve.button"), onCta: solve.start }
          : {})}
      />
    </div>
  );

  const decided = (outcome: DraftOutcome) => {
    setPanel(undefined);
    setNotice({ source: "draft", outcome });
  };
  const isLive = read.mode === "live";
  const canDrag = canPublish && desktop && isLive && !offline;
  const preview =
    flow.state !== undefined && flow.state.phase !== "previewing" && flow.state.phase !== "refused"
      ? flow.state.preview
      : undefined;
  const ghosts = preview !== undefined ? ghostsOf(preview, live.operations) : [];
  const startPreview = (req: MoveRequest) => {
    setRefusal(undefined);
    setNotice(undefined);
    void flow.preview(req);
  };
  const noticeKind =
    notice === undefined
      ? "neutral"
      : notice.source === "draft"
        ? notice.outcome.kind === "published"
          ? "ok"
          : notice.outcome.kind === "discarded"
            ? "neutral"
            : "warn"
        : notice.kind === "superseded"
          ? "warn"
          : "ok";

  return (
    <section
      className="planning-screen planning-schedule"
      data-testid="planning-schedule"
      data-mode={read.mode}
    >
      {pageHead}
      {solve.banner}
      <RunLines list={runs.data} timezone={zone} />
      {notice !== undefined ? (
        <Banner
          kind={noticeKind}
          data-testid="planning-schedule-notice"
          data-outcome={notice.source === "draft" ? notice.outcome.kind : notice.kind}
          onDismiss={() => {
            setNotice(undefined);
          }}
          dismissLabel={t("planning.common.close")}
        >
          {notice.source === "draft"
            ? t(`planning.schedule.draft.outcome.${notice.outcome.kind}`, {
                version: notice.outcome.version ?? "",
              })
            : t(`planning.schedule.move.outcome.${notice.kind}`, {
                version: notice.version ?? "",
              })}
        </Banner>
      ) : null}
      {refusal !== undefined ? (
        <Banner
          kind="warn"
          data-testid="planning-schedule-drag-refused"
          data-reason={refusal.reason}
          onDismiss={() => {
            setRefusal(undefined);
          }}
          dismissLabel={t("planning.common.close")}
        >
          {refusal.reason === "lane"
            ? t("planning.schedule.move.laneNotAllowed", {
                wc: laneName(
                  live.lanes.find((l) => l.work_center_id === refusal.laneId),
                  refusal.laneId,
                ),
              })
            : t(`planning.schedule.move.blocked.${refusal.reason}`)}
        </Banner>
      ) : null}
      {read.mode === "none" ? (
        emptyBoard
      ) : (
        <>
          {read.mode === "first_draft" && shown !== null ? (
            <Banner kind="warn" data-testid="planning-schedule-first-draft">
              {t("planning.schedule.firstDraft")}{" "}
              {canPublish ? (
                <Button
                  size="sm"
                  variant="primary"
                  data-testid="planning-schedule-first-publish"
                  onClick={() => {
                    setPanel({ kind: "draft", draft: shown });
                  }}
                >
                  {t("planning.schedule.draft.review")}
                </Button>
              ) : null}
            </Banner>
          ) : null}
          {shown !== null ? (
            <ScheduleHead
              header={shown}
              timezone={zone}
              waiting={read.mode === "live" ? waiting : null}
              onOpenDraft={(draft) => {
                setPanel({ kind: "draft", draft });
              }}
            />
          ) : null}
          {nothingPlaced ? (
            emptyBoard
          ) : (
            <>
              {desktop ? (
                <BoardControls
                  search={search}
                  days={days}
                  zoom={zoom}
                  from={from}
                  to={window.to}
                  horizon={horizon}
                  laneLabel={
                    search.wc === undefined
                      ? undefined
                      : laneName(
                          live.lanes.find((l) => l.work_center_id === search.wc),
                          search.wc,
                        )
                  }
                  onChange={setSearch}
                />
              ) : (
                <PhoneControls
                  lanes={live.lanes.map((l) => ({
                    id: l.work_center_id,
                    name: laneName(l, l.work_center_id),
                  }))}
                  lane={laneFilter ?? live.lanes[0]?.work_center_id}
                  day={from}
                  horizon={horizon}
                  onLane={(wc) => {
                    setSearch({ wc });
                  }}
                  onDay={(day) => {
                    setSearch({ from: day });
                  }}
                />
              )}
              {filtering && ops.length === 0 && unplaced.length === 0 ? (
                <div data-testid="planning-schedule-filtered-empty">
                  <EmptyState
                    title={t("planning.schedule.filteredTitle")}
                    body={t("planning.schedule.filteredBody", {
                      filters: [search.order, search.product, search.key]
                        .filter((v) => v !== undefined)
                        .join(", "),
                    })}
                    ctaLabel={t("planning.common.clearFilters")}
                    onCta={() => {
                      setSearch({ order: undefined, product: undefined, key: undefined });
                    }}
                  />
                </div>
              ) : desktop ? (
                <>
                  {flow.state !== undefined ? (
                    <ConsequencesPanel
                      state={flow.state}
                      lanes={live.lanes}
                      timezone={zone}
                      onConfirm={() => void flow.confirm()}
                      onCancel={flow.cancel}
                      onAgain={flow.again}
                    />
                  ) : canDrag ? (
                    <p className="planning-hint" data-testid="planning-schedule-drag-hint">
                      {t("planning.schedule.move.dragHint")}
                    </p>
                  ) : null}
                  <ScheduleLanes
                    lanes={lanes}
                    shiftNames={shiftNames}
                    operations={ops}
                    unplaced={unplaced}
                    overloads={openOverloads}
                    window={window}
                    timezone={zone}
                    zoom={zoom}
                    onOpen={(op) => {
                      setPanel({ kind: "op", op });
                    }}
                    onOverload={(overload) => {
                      setPanel({ kind: "overload", overload });
                    }}
                    drag={
                      canDrag && flow.state?.phase !== "committing"
                        ? {
                            onDrop: (op, startMs, laneId) => {
                              startPreview({ op, startMs, workCenterId: laneId });
                            },
                            onRefused: (_op, reason, laneId) => {
                              setRefusal({ reason, laneId });
                            },
                          }
                        : undefined
                    }
                    ghosts={ghosts}
                  />
                </>
              ) : (
                <PhoneBoard
                  laneId={laneFilter ?? live.lanes[0]?.work_center_id}
                  ops={ops}
                  unplaced={unplaced}
                  overloads={overloads.data ?? []}
                  timezone={zone}
                  onOpen={(op) => {
                    setPanel({ kind: "op", op });
                  }}
                  onOverload={(overload) => {
                    setPanel({ kind: "overload", overload });
                  }}
                />
              )}
              {live.unplaced_count > live.unplaced.length ? (
                <p className="planning-hint" data-testid="planning-schedule-unplaced-more">
                  {t("planning.schedule.unplacedMore", {
                    count: live.unplaced_count - live.unplaced.length,
                  })}
                </p>
              ) : null}
            </>
          )}
        </>
      )}
      {panel?.kind === "op" ? (
        <BlockDrawer
          op={panel.op}
          facilityId={facility.id}
          operations={live.operations}
          lanes={live.lanes}
          timezone={zone}
          move={canPublish && isLive ? (desktop ? "form" : "phone") : "none"}
          onPreview={(req) => {
            setPanel(undefined);
            startPreview(req);
          }}
          onClose={() => {
            setPanel(undefined);
          }}
        />
      ) : null}
      {panel?.kind === "overload" ? (
        <OverloadPanel
          overload={panel.overload}
          lanes={live.lanes}
          timezone={zone}
          has={perms.has}
          canReadMembers={perms.canReadMembers}
          onClose={() => {
            setPanel(undefined);
          }}
          onApplied={(applied) => {
            setPanel(undefined);
            setNotice({ source: "move", kind: "applied", version: applied.header.version });
          }}
          onRefresh={doRefresh}
          onSuperseded={() => {
            setPanel(undefined);
            overtaken();
          }}
        />
      ) : null}
      {panel?.kind === "pins" ? (
        <PinsPanel
          facilityId={facility.id}
          lanes={live.lanes}
          timezone={zone}
          canUnpin={canPublish}
          onClose={() => {
            setPanel(undefined);
          }}
          onRefresh={doRefresh}
        />
      ) : null}
      {panel?.kind === "draft" ? (
        <DraftPanel
          draft={panel.draft}
          facilityId={facility.id}
          lanes={live.lanes}
          timezone={zone}
          canDecide={canPublish}
          onClose={() => {
            setPanel(undefined);
          }}
          onDecided={decided}
          onRefresh={doRefresh}
        />
      ) : null}
    </section>
  );
}

function LaneSkeleton() {
  const { t } = useTranslation();
  return (
    <div
      className="planning-lanes-skel"
      role="status"
      aria-label={t("planning.schedule.loading")}
      data-testid="planning-schedule-loading"
    >
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="planning-lane-skel" aria-hidden="true">
          <span className="planning-lane-skel-head" />
          <span className="planning-lane-skel-track" />
        </div>
      ))}
    </div>
  );
}

/** The version, when and how it was published, the KPIs, and the New plan waiting badge. */
function ScheduleHead({
  header,
  timezone,
  waiting,
  onOpenDraft,
}: {
  header: ScheduleHeader;
  timezone: string;
  waiting: ScheduleHeader | null;
  onOpenDraft: (draft: ScheduleHeader) => void;
}) {
  const { t, i18n } = useTranslation();
  const k = header.kpis;
  const automatic = header.status === "published" && header.published_by === null;
  return (
    <div
      className="planning-schedule-head"
      data-testid="planning-schedule-header"
      data-version={header.version}
      data-status={header.status}
      data-schedule-id={header.id}
    >
      <p className="planning-last">
        <StatusChip
          variant={header.status === "published" ? "ok" : "warn"}
          data-status={header.status}
        >
          {t("planning.schedule.version", { version: header.version })}
        </StatusChip>
        {header.published_at !== null ? (
          <span data-testid="planning-schedule-published-at">
            {t("planning.schedule.publishedAt", {
              time: zonedStamp(header.published_at, i18n.language, timezone),
            })}
          </span>
        ) : (
          <span>{t("planning.schedule.draftOnly")}</span>
        )}
        {header.published_at !== null ? (
          <StatusChip
            variant="neutral"
            dot={false}
            data-testid="planning-schedule-auto"
            data-auto={String(automatic)}
          >
            {automatic
              ? t("planning.schedule.publishedAuto")
              : t("planning.schedule.publishedByPerson")}
          </StatusChip>
        ) : null}
        {waiting !== null ? (
          <Button
            size="sm"
            data-testid="planning-schedule-draft-badge"
            data-schedule-id={waiting.id}
            onClick={() => {
              onOpenDraft(waiting);
            }}
          >
            {t("planning.schedule.draft.badge", { version: waiting.version })}
          </Button>
        ) : null}
      </p>
      <StatStrip data-testid="planning-schedule-kpis">
        <StatTile
          label={t("planning.schedule.kpis.lateOrders")}
          value={k.late_orders}
          data-testid="planning-schedule-kpi-late"
        />
        <StatTile
          label={t("planning.schedule.kpis.lateDays")}
          value={k.total_late_days}
          data-testid="planning-schedule-kpi-late-days"
        />
        <StatTile
          label={t("planning.schedule.kpis.setupMinutes")}
          value={k.total_setup_minutes}
          data-testid="planning-schedule-kpi-setup"
        />
        <StatTile
          label={t("planning.schedule.kpis.overloads")}
          value={k.overloads}
          data-testid="planning-schedule-kpi-overloads"
        />
        <StatTile
          label={t("planning.schedule.kpis.unplaced")}
          value={k.unplaced}
          data-testid="planning-schedule-kpi-unplaced"
        />
      </StatStrip>
    </div>
  );
}

/** The last solve, a queued request and the failure count, from the runs read. */
function RunLines({ list, timezone }: { list: ScheduleRunList | undefined; timezone: string }) {
  const { t, i18n } = useTranslation();
  if (list === undefined) return null;
  const last = list.items[0];
  const pending = list.pending_request;
  const time = (iso: string | null) => zonedStamp(iso, i18n.language, timezone);
  return (
    <>
      {list.consecutive_failures > 0 ? (
        <Banner kind="bad" data-testid="planning-schedule-failures">
          {t("planning.schedule.failures", { count: list.consecutive_failures })}
        </Banner>
      ) : null}
      {last !== undefined ? (
        <p
          className="planning-last"
          data-testid="planning-schedule-last-solve"
          data-status={last.status}
          data-run-id={last.id}
        >
          <span>
            {t("planning.schedule.lastSolve", { time: time(last.finished_at ?? last.started_at) })}
          </span>
          <SolveTriggerChip trigger={last.trigger} />
          <SolveStatusChip status={last.status} />
          {last.status === "failed" ? <SolveErrorLine run={last} /> : null}
        </p>
      ) : null}
      {pending !== null ? (
        <p
          className="planning-note"
          role="status"
          data-testid="planning-schedule-pending"
          data-due-at={pending.due_at}
          data-urgent={String(pending.urgent)}
        >
          {pending.urgent
            ? t("planning.schedule.pendingUrgent")
            : t("planning.schedule.pending", {
                count: pending.trigger_count,
                time: time(pending.due_at),
              })}
        </p>
      ) : null}
    </>
  );
}

function BoardControls({
  search,
  days,
  zoom,
  from,
  to,
  horizon,
  laneLabel,
  onChange,
}: {
  search: ScheduleSearch;
  days: BoardDays;
  zoom: "day" | "week";
  from: string;
  to: string;
  horizon: { start: string; days: number } | undefined;
  /** The work centre the board is narrowed to, by name, when it is. */
  laneLabel: string | undefined;
  onChange: (next: Partial<ScheduleSearch>) => void;
}) {
  const { t, i18n } = useTranslation();
  const last = horizon === undefined ? undefined : addDaysIso(horizon.start, horizon.days - 1);
  const canEarlier = horizon === undefined || from > horizon.start;
  const canLater = last === undefined || to < last;
  const [order, setOrder] = useState(search.order ?? "");
  const [product, setProduct] = useState(search.product ?? "");
  const [key, setKey] = useState(search.key ?? "");
  const apply = () => {
    onChange({
      order: order.trim() || undefined,
      product: product.trim() || undefined,
      key: key.trim() || undefined,
    });
  };
  const clear = () => {
    setOrder("");
    setProduct("");
    setKey("");
    onChange({ order: undefined, product: undefined, key: undefined, wc: undefined });
  };
  const textFilter = (
    name: "order" | "product" | "key",
    value: string,
    set: (v: string) => void,
  ) => (
    <Field label={t(`planning.schedule.filters.${name}`)}>
      <input
        type="text"
        value={value}
        data-testid={`planning-schedule-filter-${name}`}
        onChange={(e) => {
          set(e.target.value);
        }}
        onBlur={apply}
      />
    </Field>
  );
  const applied: FilterChip[] = (
    [
      ["order", search.order, setOrder],
      ["product", search.product, setProduct],
      ["key", search.key, setKey],
    ] as const
  ).flatMap(([name, value, set]) =>
    value === undefined
      ? []
      : [
          {
            key: name,
            label: `${t(`planning.schedule.filters.${name}`)}: ${value}`,
            onRemove: () => {
              set("");
              onChange({ [name]: undefined });
            },
          },
        ],
  );
  if (laneLabel !== undefined) {
    applied.push({
      key: "wc",
      label: `${t("planning.schedule.filters.workCenter")}: ${laneLabel}`,
      onRemove: () => {
        onChange({ wc: undefined });
      },
    });
  }
  return (
    <div className="planning-schedule-controls" data-testid="planning-schedule-controls">
      <div className="planning-schedule-window">
        <SegmentedControl
          label={t("planning.schedule.daysLabel")}
          data-testid="planning-schedule-days"
          options={BOARD_DAYS.map((d) => ({
            value: String(d),
            label: t("planning.schedule.days", { count: d }),
          }))}
          value={String(days)}
          onChange={(v) => {
            onChange({ days: Number(v) as BoardDays });
          }}
        />
        <SegmentedControl
          label={t("planning.schedule.zoomLabel")}
          data-testid="planning-schedule-zoom"
          options={[
            { value: "week", label: t("planning.schedule.zoomWeek") },
            { value: "day", label: t("planning.schedule.zoomDay") },
          ]}
          value={zoom}
          onChange={(v) => {
            onChange({ zoom: v === "day" ? "day" : "week" });
          }}
        />
        <Button
          size="sm"
          data-testid="planning-schedule-earlier"
          disabled={!canEarlier}
          onClick={() => {
            onChange({ from: addDaysIso(from, -days) });
          }}
        >
          {t("planning.schedule.earlier")}
        </Button>
        <span
          className="planning-hint"
          data-testid="planning-schedule-range"
          data-from={from}
          data-to={to}
        >
          {t("planning.schedule.range", {
            from: formatDate(from, i18n.language),
            to: formatDate(to, i18n.language),
          })}
        </span>
        <Button
          size="sm"
          data-testid="planning-schedule-later"
          disabled={!canLater}
          onClick={() => {
            onChange({ from: addDaysIso(from, days) });
          }}
        >
          {t("planning.schedule.later")}
        </Button>
      </div>
      {/* One FilterBar row (D-450): the order and the product inline, the
          setup key behind "Filters", and what is applied as chips. A box
          applies on Enter or when it loses focus, and the panel applies what
          its boxes hold whenever it closes, a press outside included. */}
      <form
        className="planning-schedule-filters"
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
      >
        <FilterBar
          data-testid="planning-schedule-filterbar"
          filters={{
            inline: (
              <>
                {textFilter("order", order, setOrder)}
                {textFilter("product", product, setProduct)}
              </>
            ),
            more: textFilter("key", key, setKey),
            label: t("planning.common.filtersCount", { count: applied.length }),
            title: t("planning.common.filters"),
            doneLabel: t("planning.common.done"),
          }}
          chips={applied}
          removeLabel={(chip) => t("planning.common.removeFilter", { filter: chip.label })}
          clearAll={{ label: t("planning.common.clearFilters"), onClear: clear }}
          onClose={apply}
        />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </div>
  );
}

/** The phone's work centre selector and day stepper (§4.6 "At 412 px"). */
function PhoneControls({
  lanes,
  lane,
  day,
  horizon,
  onLane,
  onDay,
}: {
  lanes: readonly { id: string; name: string }[];
  lane: string | undefined;
  day: string;
  horizon: { start: string; days: number } | undefined;
  onLane: (id: string) => void;
  onDay: (day: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const last = horizon === undefined ? undefined : addDaysIso(horizon.start, horizon.days - 1);
  return (
    <div className="planning-schedule-phone-controls" data-testid="planning-schedule-phone">
      <Field label={t("planning.schedule.phone.lane")}>
        <Select
          value={lane ?? ""}
          data-testid="planning-schedule-phone-lane"
          onChange={(e) => {
            onLane(e.target.value);
          }}
        >
          {lanes.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Select>
      </Field>
      <div className="planning-day-stepper">
        <Button
          size="sm"
          data-testid="planning-schedule-phone-prev"
          aria-label={t("planning.schedule.phone.prevDay")}
          disabled={horizon !== undefined && day <= horizon.start}
          onClick={() => {
            onDay(addDaysIso(day, -1));
          }}
        >
          ‹
        </Button>
        <span
          className="planning-day-label"
          data-testid="planning-schedule-phone-day"
          data-day={day}
        >
          {formatDate(day, i18n.language)}
        </span>
        <Button
          size="sm"
          data-testid="planning-schedule-phone-next"
          aria-label={t("planning.schedule.phone.nextDay")}
          disabled={last !== undefined && day >= last}
          onClick={() => {
            onDay(addDaysIso(day, 1));
          }}
        >
          ›
        </Button>
      </div>
    </div>
  );
}

function PhoneBoard({
  laneId,
  ops,
  unplaced,
  overloads,
  timezone,
  onOpen,
  onOverload,
}: {
  laneId: string | undefined;
  ops: readonly ScheduleOperation[];
  unplaced: readonly ScheduleOperation[];
  overloads: readonly ScheduleOverload[];
  timezone: string;
  onOpen: (op: ScheduleOperation) => void;
  onOverload: (overload: ScheduleOverload) => void;
}) {
  const { t, i18n } = useTranslation();
  const mine = overloads.filter((o) => o.work_center_id === laneId);
  return (
    <div className="planning-schedule-phone-board" data-testid="planning-schedule-phone-board">
      <ScheduleHatchDefs />
      <ScheduleCards
        ops={ops.filter((o) => o.work_center_id === laneId)}
        unplaced={unplaced.filter((o) => o.work_center_id === laneId)}
        timezone={timezone}
        onOpen={onOpen}
      />
      {mine.length > 0 ? (
        <div className="planning-schedule-cards" data-testid="planning-schedule-phone-overloads">
          <h2 className="planning-subhead">{t("planning.schedule.phone.overloads")}</h2>
          {mine.map((o) => (
            <button
              key={o.id}
              type="button"
              className="planning-op-card is-late"
              data-testid="planning-schedule-overload"
              data-week={o.week_start}
              data-hours={o.overload_hours}
              onClick={() => {
                onOverload(o);
              }}
            >
              <span className="planning-op-card-title">
                {t("planning.schedule.overload.marker", { hours: o.overload_hours })}
              </span>
              <span className="planning-op-card-sub">{weekLabel(o.week_start, i18n.language)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
